import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { Container, Flex } from '@stoplight/mosaic'
import {
  InlineRefResolverProvider,
  NonIdealState,
  RoutingProps,
  useParsedValue,
} from '@stoplight/elements-core'

import { transformOasToServiceNode } from '../utils/oas'

import { Content } from './Content'
import { ContentLoader } from './ContentLoader'
import { useExportDocumentProps } from '../hooks/useExportDocumentProps'

interface APIProps extends RoutingProps {
  apiDescriptionUrl: string
  basePath?: string
}

export default function API({
  apiDescriptionUrl = '',
  basePath = '',
}: APIProps) {
  const { data: fetchedDocument, error } = useQuery(
    [apiDescriptionUrl],
    () =>
      fetch(apiDescriptionUrl).then((res) => {
        if (res.ok) {
          return res.text()
        }
        throw new Error(
          `Unable to load description document, status code: ${res.status}`
        )
      }),
    {
      enabled: apiDescriptionUrl !== '',
    }
  )

  const parsedDocument = useParsedValue(fetchedDocument)

  const serviceNode = useMemo(
    () => transformOasToServiceNode(parsedDocument),
    [parsedDocument]
  )

  const exportProps = useExportDocumentProps({
    name: `ninjaOne-API-${basePath.replaceAll('/', '')}`,
    originalDocument: fetchedDocument,
    bundledDocument: parsedDocument,
  })

  if (error) {
    return (
      <Flex justify="center" alignItems="center" w="full" h="full">
        <NonIdealState
          title="The API document could not be loaded"
          icon="exclamation-triangle"
        />
      </Flex>
    )
  }

  if (!parsedDocument) {
    return (
      <Container bg="canvas" size="lg" mt={6}>
        <Flex flexDirection="col" bg="canvas" w="full" alignItems="center">
          <ContentLoader />
        </Flex>
      </Container>
    )
  }

  if (!serviceNode) {
    return (
      <Flex justify="center" alignItems="center" w="full" h="full">
        <NonIdealState title="Failed to parse the API specification document" />
      </Flex>
    )
  }

  return (
    <InlineRefResolverProvider document={parsedDocument}>
      <Content {...{ basePath, serviceNode, exportProps }} />
    </InlineRefResolverProvider>
  )
}
